import { reactive } from 'vue';
import { toast } from 'vue-sonner';
import {
    MESSAGE_ATTACHMENT_MAX_COUNT,
    messageAttachmentFileScope,
    toFileAttachment,
    validateMessageAttachment,
} from '~/lib/messageAttachments';
import type { FileAttachment, UploadedFile } from '~/types/models';

export type FileAttachmentDraft = {
    id: string;
    file: File;
    progress: number;
    status: 'uploading' | 'ready' | 'error';
    error?: string;
    storedFile?: UploadedFile;
};

export function createFileAttachmentDraft(file: File): FileAttachmentDraft {
    return reactive({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        progress: 0,
        status: 'uploading',
    });
}

export function useFileAttachments(options: {
    tenantId: MaybeRefOrGetter<string>;
    clientId?: MaybeRefOrGetter<string>;
    portal?: boolean;
    entityType?: MaybeRefOrGetter<UploadedFile['entityType']>;
    entityId?: MaybeRefOrGetter<string>;
    contextLabel?: string;
}) {
    const filesStore = useFilesStore();
    const portalFilesStore = useClientPortalFilesStore();
    const drafts = ref<FileAttachmentDraft[]>([]);

    const readyAttachments = computed<FileAttachment[]>(() => (
        drafts.value
            .filter((draft) => draft.status === 'ready' && draft.storedFile)
            .map((draft) => toFileAttachment(draft.storedFile!))
    ));
    const uploading = computed(() => drafts.value.some((draft) => draft.status === 'uploading'));
    const hasErrors = computed(() => drafts.value.some((draft) => draft.status === 'error'));

    async function uploadDraft(draft: FileAttachmentDraft) {
        const clientId = options.clientId ? toValue(options.clientId) : '';
        const tenantId = toValue(options.tenantId);

        draft.status = 'uploading';
        draft.error = '';
        draft.progress = 0;

        try {
            const explicitEntityType = options.entityType ? toValue(options.entityType) : '';
            const explicitEntityId = options.entityId ? toValue(options.entityId) : '';
            if (!explicitEntityId && !clientId) throw new Error('Choose a Client before attaching files.');
            if (!tenantId) throw new Error('The workspace could not be resolved. Refresh the page and try again.');

            const fileScope = explicitEntityType && explicitEntityId
                ? { entityType: explicitEntityType, entityId: explicitEntityId }
                : messageAttachmentFileScope(clientId);
            let fileId = '';
            if (options.portal) {
                const scopedEntityKey = portalFilesStore.scopedEntityKey(tenantId, fileScope.entityType, fileScope.entityId);
                fileId = await portalFilesStore.uploadForEntity({
                    tenantId,
                    ...fileScope,
                    file: draft.file,
                    onProgress: (progress) => { draft.progress = progress; },
                });
                draft.storedFile = portalFilesStore.filesByEntity[scopedEntityKey]?.find((file) => file.id === fileId);
            } else {
                fileId = await filesStore.uploadFile(fileScope.entityType, fileScope.entityId, draft.file, {
                    onProgress: (progress) => { draft.progress = progress; },
                });
                draft.storedFile = filesStore.filesByEntity[`${fileScope.entityType}:${fileScope.entityId}`]?.find((file) => file.id === fileId);
            }

            if (!draft.storedFile) throw new Error('The upload completed but its file record could not be loaded.');
            draft.progress = 100;
            draft.status = 'ready';
        } catch (error: any) {
            draft.status = 'error';
            draft.error = error?.message || 'Upload failed.';
            toast.error(draft.error);
        }
    }

    async function addFiles(files: File[]) {
        const remaining = MESSAGE_ATTACHMENT_MAX_COUNT - drafts.value.length;
        if (remaining <= 0) {
            toast.error(`You can attach up to ${MESSAGE_ATTACHMENT_MAX_COUNT} files to one ${options.contextLabel || 'message'}.`);
            return;
        }

        for (const file of files.slice(0, remaining)) {
            const validationError = validateMessageAttachment(file);
            if (validationError) {
                toast.error(validationError);
                continue;
            }

            const draft = createFileAttachmentDraft(file);
            drafts.value.push(draft);
            void uploadDraft(draft);
        }

        if (files.length > remaining) toast.error(`Only ${MESSAGE_ATTACHMENT_MAX_COUNT} attachments can be added.`);
    }

    async function removeDraft(id: string) {
        const draft = drafts.value.find((entry) => entry.id === id);
        if (!draft) return;
        if (draft.storedFile) {
            if (options.portal) await portalFilesStore.deleteForClient(toValue(options.tenantId), draft.storedFile);
            else await filesStore.deleteFile(draft.storedFile.entityType, draft.storedFile.entityId, draft.storedFile);
        }
        drafts.value = drafts.value.filter((entry) => entry.id !== id);
    }

    function retryDraft(id: string) {
        const draft = drafts.value.find((entry) => entry.id === id);
        if (draft) void uploadDraft(draft);
    }

    function clearDrafts() {
        drafts.value = [];
    }

    return { drafts, readyAttachments, uploading, hasErrors, addFiles, removeDraft, retryDraft, clearDrafts };
}
